// CourseCanvasDrawer — the shared course create/edit surface.
//
// A full-bleed "island" modal (matching the 3a needs edit surface) used for BOTH
// creating and editing a course. It's a thin shell around ManualModuleForm, which
// owns the whole presentation (header, side-rail Link seed, TipTap description,
// curriculum timeline, footer, unsaved-changes guard):
//   - create: enableSeed (side-rail Link seed panel)
//   - edit:   loads the module by id and seeds initial values
// The header actions ("Chatta med Link", "Visa som student") live here because
// they need askLink + the student-preview overlay, and are passed into the form.
// Reused by the 2-door chooser (create) and the /onboarding/modules/:id route (edit).

import React, { useEffect, useRef, useState } from 'react';
import { useLinkLauncher } from '@/contexts/LinkLauncherContext';
import { ChattaMedLinkButton } from '@/components/custom-ui/ChattaMedLinkButton';
import { OutlineButton } from '@/components/custom-ui/action-button';
import { IslandSlideOver } from '@/components/custom-ui/IslandSlideOver';
import { LoadingState } from '@/components/custom-ui/empty-state';
import { moduleApi } from '@/lib/api/moduleApi';
import { ModuleFullResponse } from '@/lib/models/module';
import { ModuleStepInput, ModuleStepType } from '@/lib/models/onboarding';
import ManualModuleForm from './ManualModuleForm';

interface CourseCanvasDrawerProps {
    // Edit mode when set; otherwise create mode.
    moduleId?: string;
    onClose: () => void;
    // Called after a successful create/save (e.g. to refetch a list).
    onSaved?: () => void;
}

const CourseCanvasDrawer: React.FC<CourseCanvasDrawerProps> = ({ moduleId, onClose, onSaved }) => {
    const { stageLink } = useLinkLauncher();
    const isEdit = !!moduleId;
    const [module, setModule] = useState<ModuleFullResponse | null>(null);
    const [isLoading, setIsLoading] = useState(isEdit);
    const [error, setError] = useState<string | null>(null);

    // The form registers its unsaved-changes guard here so the modal's backdrop /
    // esc close routes through the same "spara ändringar?" prompt.
    const requestCloseRef = useRef<(() => void) | null>(null);
    const guardedClose = () => (requestCloseRef.current ?? onClose)();

    useEffect(() => {
        if (!moduleId) return;
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        moduleApi.getModuleFull(moduleId)
            .then((data) => {
                if (cancelled) return;
                if (!data) setError('Modulen hittades inte');
                else setModule(data);
            })
            .catch((e: any) => { if (!cancelled) setError(e?.message || 'Kunde inte ladda modulen'); })
            .finally(() => { if (!cancelled) setIsLoading(false); });
        return () => { cancelled = true; };
    }, [moduleId]);

    const initialSteps: ModuleStepInput[] = (module?.steps || [])
        .slice()
        .sort((a, b) => a.step_order - b.step_order)
        .map((step) => ({
            step_type: step.step_type as ModuleStepType,
            step_title: step.step_title,
            content: step.content,
            // Round-trip the fields the editor doesn't surface but the step
            // owns, so an edit (or a no-op save) preserves them instead of
            // blanking evaluation_mode/sub_points/phase to defaults.
            evaluation_mode: step.evaluation_mode,
            sub_points: step.sub_points,
            phase: step.phase,
        }));

    // Edit-only header action, handed to the form's header bar. ("Visa som
    // student" lives in the form itself — it previews the live editor state.)
    const editActions = isEdit && module ? (
        <ChattaMedLinkButton
            onClick={() => {
                // #301 — staged (unsent) @-tagged prompt referencing this modul.
                stageLink({
                    reference: { type: 'module', id: module.id },
                    label: module.module_name,
                    prompt: 'Hjälp mig att jobba vidare med den här modulen.',
                });
            }}
        />
    ) : undefined;

    return (
        <IslandSlideOver onClose={guardedClose} fullScreen zClass="z-50">
            {isEdit && isLoading ? (
                <div className="flex flex-col h-full"><LoadingState message="Laddar modul..." /></div>
            ) : error ? (
                <div className="flex flex-col h-full items-center justify-center p-8 text-center text-gray-600">{error}</div>
            ) : isEdit && module ? (
                <ManualModuleForm
                    existingModuleId={module.id}
                    initialModuleName={module.module_name}
                    initialModuleDescription={module.module_description}
                    initialEstimatedDuration={module.estimated_duration || ''}
                    initialSteps={initialSteps}
                    headerActions={editActions}
                    requestCloseRef={requestCloseRef}
                    onClose={onClose}
                    onSuccess={() => { onSaved?.(); onClose(); }}
                />
            ) : (
                <ManualModuleForm
                    enableSeed
                    requestCloseRef={requestCloseRef}
                    onClose={onClose}
                    onSuccess={() => { onSaved?.(); onClose(); }}
                />
            )}
        </IslandSlideOver>
    );
};

export default CourseCanvasDrawer;
